import {match} from 'ts-pattern';

import {findIndex, isNil, propEq} from 'ramda';

import {FinancingCompositionLayer, FinancingCompositionScene} from '@dms/api/shared';

import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {
  CompositionEditorAction,
  LayerAlignment,
  LayerBox,
  LayerReorder,
} from '../types/CompositionEditorAction';
import {CompositionEditorState} from '../types/CompositionEditorState';
import {applyLayerPatch} from './applyLayerPatch';
import {clampLayerToCanvas} from './clampLayerToCanvas';
import {hasPhotoLayer} from './hasPhotoLayer';

const clampCanvasSize = (value: number) =>
  Math.min(
    Math.max(Math.round(value), compositionEditorLimits.minCanvasSize),
    compositionEditorLimits.maxCanvasSize
  );

const withLayerBox = (
  layer: FinancingCompositionLayer,
  box: LayerBox
): FinancingCompositionLayer => ({
  ...layer,
  ...box,
});

const truncateText = (layer: FinancingCompositionLayer): FinancingCompositionLayer =>
  layer.type === 'text' && layer.text.length > compositionEditorLimits.maxTextLength
    ? {...layer, text: layer.text.slice(0, compositionEditorLimits.maxTextLength)}
    : layer;

export const canAddLayers = (scene: FinancingCompositionScene, count: number) =>
  scene.layers.length + count <= compositionEditorLimits.maxLayers;

// A scene holds at most one photo slot (BE rejects more), so adding or duplicating one is a no-op
// when the scene already has it.
const wouldExceedPhotoLayers = (
  scene: FinancingCompositionScene,
  added: FinancingCompositionLayer[]
) =>
  (hasPhotoLayer(scene.layers) ? 1 : 0) + added.filter((layer) => layer.type === 'photo').length >
  1;

const mapLayer = (
  scene: FinancingCompositionScene,
  layerId: string,
  mapper: (layer: FinancingCompositionLayer) => FinancingCompositionLayer
): FinancingCompositionScene => ({
  ...scene,
  layers: scene.layers.map((layer) => (layer.id === layerId ? mapper(layer) : layer)),
});

const alignBox = (
  layer: FinancingCompositionLayer,
  canvas: FinancingCompositionScene['canvas'],
  alignment: LayerAlignment
): LayerBox =>
  match<LayerAlignment, LayerBox>(alignment)
    .with('left', () => ({...layer, x: 0}))
    .with('hCenter', () => ({...layer, x: Math.round((canvas.width - layer.width) / 2)}))
    .with('right', () => ({...layer, x: canvas.width - layer.width}))
    .with('top', () => ({...layer, y: 0}))
    .with('vCenter', () => ({...layer, y: Math.round((canvas.height - layer.height) / 2)}))
    .with('bottom', () => ({...layer, y: canvas.height - layer.height}))
    .exhaustive();

const reorderLayers = (
  layers: FinancingCompositionLayer[],
  layerId: string,
  reorder: LayerReorder
): FinancingCompositionLayer[] => {
  const index = findIndex(propEq(layerId, 'id'), layers);
  const movedLayer = layers.find((layer) => layer.id === layerId);
  if (index < 0 || isNil(movedLayer)) {
    return layers;
  }
  const lastIndex = layers.length - 1;
  const targetIndex = match<LayerReorder, number>(reorder)
    .with('bringForward', () => Math.min(index + 1, lastIndex))
    .with('sendBackward', () => Math.max(index - 1, 0))
    .with('toFront', () => lastIndex)
    .with('toBack', () => 0)
    .exhaustive();
  if (targetIndex === index) {
    return layers;
  }
  const withoutLayer = layers.filter((layer) => layer.id !== layerId);
  return [...withoutLayer.slice(0, targetIndex), movedLayer, ...withoutLayer.slice(targetIndex)];
};

const pushHistory = (
  state: CompositionEditorState,
  scene: FinancingCompositionScene,
  selectedLayerId: string | null = state.selectedLayerId
): CompositionEditorState => {
  if (scene === state.scene) {
    return state;
  }
  return {
    scene,
    selectedLayerId,
    past: [...state.past, state.scene].slice(-compositionEditorLimits.historyLimit),
    future: [],
  };
};

export const createCompositionEditorState = (
  scene: FinancingCompositionScene
): CompositionEditorState => ({
  scene,
  selectedLayerId: null,
  past: [],
  future: [],
});

export const compositionEditorReducer = (
  state: CompositionEditorState,
  action: CompositionEditorAction
): CompositionEditorState =>
  match<CompositionEditorAction, CompositionEditorState>(action)
    .with({type: 'addLayers'}, ({layers}) => {
      if (
        !canAddLayers(state.scene, layers.length) ||
        wouldExceedPhotoLayers(state.scene, layers)
      ) {
        return state;
      }
      const clampedLayers = layers.map((layer) =>
        withLayerBox(truncateText(layer), clampLayerToCanvas(layer, state.scene.canvas))
      );
      const topLayer = clampedLayers[clampedLayers.length - 1];
      return pushHistory(
        state,
        {...state.scene, layers: [...state.scene.layers, ...clampedLayers]},
        topLayer?.id ?? state.selectedLayerId
      );
    })
    .with({type: 'updateLayer'}, ({layerId, patch}) =>
      pushHistory(
        state,
        mapLayer(state.scene, layerId, (layer) => {
          const patched = truncateText(applyLayerPatch(layer, patch));
          return withLayerBox(patched, clampLayerToCanvas(patched, state.scene.canvas));
        })
      )
    )
    .with({type: 'moveLayer'}, ({layerId, dx, dy}) =>
      pushHistory(
        state,
        mapLayer(state.scene, layerId, (layer) =>
          withLayerBox(
            layer,
            clampLayerToCanvas({...layer, x: layer.x + dx, y: layer.y + dy}, state.scene.canvas)
          )
        )
      )
    )
    .with({type: 'resizeLayer'}, ({layerId, box}) =>
      pushHistory(
        state,
        mapLayer(state.scene, layerId, (layer) =>
          withLayerBox(layer, clampLayerToCanvas(box, state.scene.canvas))
        )
      )
    )
    .with({type: 'removeLayer'}, ({layerId}) =>
      pushHistory(
        state,
        {...state.scene, layers: state.scene.layers.filter((layer) => layer.id !== layerId)},
        state.selectedLayerId === layerId ? null : state.selectedLayerId
      )
    )
    // A deleted uploaded font falls back to the built-in font on every text layer that used it,
    // in one history entry, so no layer keeps sending the deleted font id to the server.
    .with({type: 'clearDeletedFont'}, ({fontId}) => {
      if (!state.scene.layers.some((layer) => layer.type === 'text' && layer.fontId === fontId)) {
        return state;
      }
      return pushHistory(state, {
        ...state.scene,
        layers: state.scene.layers.map((layer) =>
          layer.type === 'text' && layer.fontId === fontId ? {...layer, fontId: null} : layer
        ),
      });
    })
    .with({type: 'duplicateLayer'}, ({layerId, newLayerId}) => {
      const source = state.scene.layers.find((layer) => layer.id === layerId);
      if (
        isNil(source) ||
        !canAddLayers(state.scene, 1) ||
        wouldExceedPhotoLayers(state.scene, [source])
      ) {
        return state;
      }
      const duplicate = withLayerBox(
        {...source, id: newLayerId},
        clampLayerToCanvas({...source, x: source.x + 16, y: source.y + 16}, state.scene.canvas)
      );
      return pushHistory(
        state,
        {...state.scene, layers: [...state.scene.layers, duplicate]},
        newLayerId
      );
    })
    .with({type: 'reorderLayer'}, ({layerId, reorder}) => {
      const layers = reorderLayers(state.scene.layers, layerId, reorder);
      return layers === state.scene.layers ? state : pushHistory(state, {...state.scene, layers});
    })
    .with({type: 'alignLayer'}, ({layerId, alignment}) =>
      pushHistory(
        state,
        mapLayer(state.scene, layerId, (layer) =>
          withLayerBox(layer, alignBox(layer, state.scene.canvas, alignment))
        )
      )
    )
    .with({type: 'setCanvasSize'}, ({width, height}) => {
      const canvas = {
        ...state.scene.canvas,
        width: clampCanvasSize(width),
        height: clampCanvasSize(height),
      };
      return pushHistory(state, {
        ...state.scene,
        canvas,
        layers: state.scene.layers.map((layer) =>
          withLayerBox(layer, clampLayerToCanvas(layer, canvas))
        ),
      });
    })
    .with({type: 'setBackground'}, ({background}) =>
      pushHistory(state, {...state.scene, canvas: {...state.scene.canvas, background}})
    )
    // One history entry, so undo brings the previous scene back.
    .with({type: 'replaceScene'}, ({scene}) => pushHistory(state, scene, null))
    .with({type: 'selectLayer'}, ({layerId}) => ({...state, selectedLayerId: layerId}))
    .with({type: 'undo'}, () => {
      const previous = state.past[state.past.length - 1];
      if (isNil(previous)) {
        return state;
      }
      return {
        scene: previous,
        selectedLayerId: previous.layers.some(propEq(state.selectedLayerId, 'id'))
          ? state.selectedLayerId
          : null,
        past: state.past.slice(0, -1),
        future: [state.scene, ...state.future],
      };
    })
    .with({type: 'redo'}, () => {
      const [next, ...future] = state.future;
      if (isNil(next)) {
        return state;
      }
      return {
        scene: next,
        selectedLayerId: next.layers.some(propEq(state.selectedLayerId, 'id'))
          ? state.selectedLayerId
          : null,
        past: [...state.past, state.scene],
        future,
      };
    })
    .exhaustive();
